import { useEffect, useState, useSyncExternalStore } from "react";

/**
 * Hook to track a CSS media query.
 * @param query - The media query string (e.g., "(max-width: 640px)")
 * @returns boolean indicating if the media query matches
 */
export const useMediaQuery = (query: string): boolean => {
  const getSnapshot = () => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  };

  const getServerSnapshot = () => false;

  const subscribe = (callback: () => void) => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return () => {};
    }
    const mediaQuery = window.matchMedia(query);

    // Modern browsers support addEventListener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", callback);
      return () => mediaQuery.removeEventListener("change", callback);
    }

    // Fallback for older browsers
    mediaQuery.addListener(callback);
    return () => mediaQuery.removeListener(callback);
  };

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};

/**
 * Hook to detect if viewport is "mobile" sized (< 640px / Tailwind 'sm').
 * Consistent with Tailwind's default breakpoints.
 */
export const useIsMobile = (): boolean => {
  return useMediaQuery("(max-width: 639px)");
};

/**
 * Hook to detect if viewport is "desktop" sized (>= 1024px / Tailwind 'lg').
 * Matches the breakpoint where the sidebar becomes visible.
 */
export const useIsDesktop = (): boolean => {
  return useMediaQuery("(min-width: 1024px)");
};
